import type { PlaycastWebrtcStats } from './typesMedia';
import constants from '@playcastdotio/GlobalConfig';

type MetricStats = {
  median: number;
  mean: number;
  min: number;
  max: number;
};

const state: {
  [peerId: string]: {
    enabled: boolean;
    buffer: PlaycastWebrtcStats[];
  };
} = {};

const isRelease = constants.BRANCH === 'release';

const DEFAULT_SAMPLE_RATE = isRelease ? 0.1 : 1;
const BATCH_SIZE = 10;

const shouldSample = (sampleRate: number = DEFAULT_SAMPLE_RATE): boolean => {
  return Math.random() < sampleRate;
};

const calculateMedian = (numbers: number[]): number => {
  const sorted = [...numbers].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const calculateStats = (numbers: number[]): MetricStats => {
  const sum = numbers.reduce((a, b) => a + b, 0);
  return {
    median: calculateMedian(numbers),
    mean: sum / numbers.length,
    min: Math.min(...numbers),
    max: Math.max(...numbers),
  };
};

const aggregateMetrics = (stats: PlaycastWebrtcStats[]) => {
  const metrics = {
    rtt: stats.map((s) => s.candidatePair.currentRoundTripTime * 1000),
    jitter: stats.map((s) => s.rtp.inbound[0]?.jitter || 0),
    packetLoss: stats.map((s) => s.quality.rawStats.packetLossPercentage),
    fps: stats.map((s) => s.quality.rawStats.avgFrameRate),
    bitrate: stats.map((s) => s.quality.rawStats.bitsPerSecond / 1000),
    nacks: stats.map((s) => s.quality.rawStats.recentNackDelta),
    freezes: stats.map((s) => s.quality.rawStats.recentFreezeDelta),
    bytesReceived: stats.map((s) => s.transport.bytesReceived),
    bytesSent: stats.map((s) => s.transport.bytesSent),
  };

  return {
    rtt: calculateStats(metrics.rtt),
    jitter: calculateStats(metrics.jitter),
    packetLoss: calculateStats(metrics.packetLoss),
    fps: calculateStats(metrics.fps),
    bitrate: calculateStats(metrics.bitrate),
    nacks: calculateStats(metrics.nacks),
    freezes: calculateStats(metrics.freezes),
    bytesReceived: calculateStats(metrics.bytesReceived),
    bytesSent: calculateStats(metrics.bytesSent),
  };
};

export const startMetrics = (peerId: string, sampleRate?: number) => {
  return;
  // const parsedPeerId = peerId.split('_').pop();
  // if (!parsedPeerId) return;

  // if (!shouldSample(sampleRate) || !(window as any).Sentry) {
  //   console.log('[Sentry] Metrics collection not enabled for peer', parsedPeerId);
  //   return;
  // }

  // console.log('[Sentry] Starting metrics for peer', parsedPeerId);
  // state[parsedPeerId] = {
  //   enabled: true,
  //   buffer: [],
  // };
};

export const collectMetrics = (stats: PlaycastWebrtcStats, peerId: string) => {
  return;
  // const parsedPeerId = peerId.split('_').pop();
  // if (!parsedPeerId) {
  //   console.error('[Sentry] Failed to parse peerId', peerId);
  //   return;
  // }
  // const peerState = state[parsedPeerId];
  // if (!peerState?.enabled || !(window as any).Sentry) {
  //   console.log('[Sentry] Metrics collection not enabled for peer', parsedPeerId);
  //   return;
  // }

  // peerState.buffer.push(stats);

  // if (peerState.buffer.length >= BATCH_SIZE) {
  //   const aggregated = aggregateMetrics(peerState.buffer);
  //   const lastStat = peerState.buffer[peerState.buffer.length - 1];
  //   console.log('[Sentry] Sending batch metrics for peer', parsedPeerId, aggregated);

  //   (window as any).Sentry.startSpan(
  //     {
  //       name: 'webrtc.metrics.batch',
  //       op: 'measure',
  //       attributes: {
  //         parsedPeerId,
  //         'webrtc.ice_state': lastStat.transport.iceState,
  //         'webrtc.connection_state': lastStat.candidatePair.state,
  //         'webrtc.sample_size': BATCH_SIZE,
  //       },
  //     },
  //     (_span: any) => {
  //       // Set primary measurements using medians
  //       (window as any).Sentry.setMeasurement('rtt_ms', aggregated.rtt.median, 'millisecond');
  //       (window as any).Sentry.setMeasurement('fps', aggregated.fps.median, 'fps');
  //       (window as any).Sentry.setMeasurement('bitrate_kbps', aggregated.bitrate.median, 'kbps');
  //       (window as any).Sentry.setMeasurement('packet_loss', aggregated.packetLoss.median, 'percent');
  //     },
  //   );

  //   peerState.buffer = [];
  // }

  // if (stats.quality.recommendation !== 'maintain') {
  //   (window as any).Sentry.startSpan(
  //     {
  //       name: 'webrtc.quality_change',
  //       op: 'state_change',
  //       attributes: {
  //         parsedPeerId,
  //         'quality.change': stats.quality.recommendation,
  //         'quality.reason': stats.quality.reason,
  //       },
  //     },
  //     () => {
  //       return;
  //     },
  //   );
  // }
};

export const stopMetrics = (peerId: string) => {
  if (!(window as any).Sentry) return;
  const parsedPeerId = peerId.split('_').pop();
  if (!parsedPeerId) return;
  console.log('[Sentry] Stopping metrics for peer', parsedPeerId);
  const peerState = state[parsedPeerId];
  if (peerState?.buffer.length > 0) {
    const aggregated = aggregateMetrics(peerState.buffer);
    const lastStat = peerState.buffer[peerState.buffer.length - 1];

    (window as any).Sentry.startSpan(
      {
        name: 'webrtc.metrics.final_batch',
        op: 'measure',
        attributes: {
          parsedPeerId,
          'webrtc.rtt.median': aggregated.rtt.median,
          'webrtc.rtt.min': aggregated.rtt.min,
          'webrtc.rtt.max': aggregated.rtt.max,
          'webrtc.jitter.median': aggregated.jitter.median,
          'webrtc.jitter.min': aggregated.jitter.min,
          'webrtc.jitter.max': aggregated.jitter.max,
          'webrtc.fps.median': aggregated.fps.median,
          'webrtc.fps.min': aggregated.fps.min,
          'webrtc.fps.max': aggregated.fps.max,
          'webrtc.ice_state': lastStat.transport.iceState,
          'webrtc.connection_state': lastStat.candidatePair.state,
          'webrtc.sample_size': peerState.buffer.length,
        },
      },
      () => {
        return;
      },
    );
  }
  delete state[parsedPeerId];
};
